/**
 * engine/apiClient — client HTTP de l'admin (headless, NEUTRE).
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * MOTEUR — futur `@mostajs/app-admin` (N1). AUCUN couplage :
 *  • `baseUrl` est INJECTÉ (pas de lecture d'env de framework — l'app le fournit,
 *    qu'elle soit CRA `REACT_APP_*`, Vite `import.meta.env`, Next, etc.) ;
 *  • les `paths` par défaut sont le **protocole app-admin ↔ serveur** ; une app à
 *    préfixes différents les surcharge. PUR CLIENT (D1), admin transitoire (D10).
 * Séparation modules : `login/me/changePassword` → `@mostajs/auth-ui` ;
 * `getConfig/saveConfig/getSchema/upload/history/restore` → `@mostajs/app-admin`.
 */
import axios from 'axios';

/** Protocole app-admin ↔ serveur (chemins sous `<baseUrl>/api`). Surchargeable. */
export const DEFAULT_PATHS = {
  login: '/auth/login',
  me: '/auth/me',
  changePassword: '/users/change-password',
  schema: '/config/schema',
  config: '/config',
  history: '/config/history',
  restore: '/config/restore', // + /:id
  users: '/users',            // + /:id, /:id/deactivate
  audit: '/audit',            // + /export.csv
};

/** Crée le client. `{ baseUrl, getToken, paths? }` — tout injecté, rien lu d'un env. */
export function createAdminApi({ baseUrl, getToken, paths } = {}) {
  const base = String(baseUrl || '').replace(/\/+$/, '');
  const p = { ...DEFAULT_PATHS, ...(paths || {}) };
  const http = axios.create({ baseURL: `${base}/api`, timeout: 30000 });

  http.interceptors.request.use((cfg) => {
    const token = getToken?.();
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
  });

  return {
    apiBase: base,
    // — Auth (surface d'@mostajs/auth-ui) —
    async login(username, password) {
      const { data } = await http.post(p.login, { username, password });
      return data.data; // { token, user }
    },
    async me() {
      const { data } = await http.get(p.me);
      return data.data; // user courant (rôle + mustChangePassword)
    },
    async changePassword(currentPassword, newPassword) {
      const { data } = await http.post(p.changePassword, { currentPassword, newPassword });
      return data;
    },
    // — Config (surface d'@mostajs/app-admin) —
    async getSchema() {
      const { data } = await http.get(p.schema);
      return data.data;
    },
    async getConfig() {
      const { data } = await http.get(p.config);
      return data.data;
    },
    async saveConfig(patch) {
      const { data } = await http.put(p.config, patch);
      return data.data;
    },
    // Seed des affichages (profils par NOM ; hors arbre config). Voir docs/evolutions-2026-07/04 §3.
    async getDisplaysSeed() {
      const { data } = await http.get('/displays/seed');
      return data.data;
    },
    async applyDisplaysSeed(profiles) {
      const { data } = await http.post('/displays/seed', { profiles });
      return data.data;
    },
    // — Écrans du site (admin/manager) —
    //
    // Ces appels passent par le MÊME `http` que le reste : en LAN (admin.exe) ils vont
    // droit au serveur du site ; depuis la console centrale, `baseUrl` vaut déjà
    // `/api/sites/<id>/proxy`. L'approbation (`enabled`), elle, n'est PAS ici : le
    // serveur ne l'accepte que sur ordre signé par l'éditeur (cf. DisplaysApproval).
    async listDisplays() {
      const { data } = await http.get('/displays');
      return data.data; // [{ displayId, name, enabled, sound, services, online, … }]
    },
    /** « 3 inclus, 2 utilisés » — explique pourquoi un écran reste en attente. */
    async getDisplaysQuota() {
      const { data } = await http.get('/displays/quota');
      return data.data; // { quota, utilises, restants, depasse }
    },
    /** Nom / son / services. `enabled` est refusé par le serveur, volontairement. */
    async updateDisplay(displayId, patch) {
      const { data } = await http.put(`/displays/${encodeURIComponent(displayId)}`, patch);
      return data.data;
    },
    async setAllDisplaysSound(sound) {
      const { data } = await http.post('/displays/sound-all', { sound });
      return data.data;
    },
    /** Test audio joué SUR l'écran visé (`kind` : 'beep' | 'voice'). */
    async testDisplay(displayId, kind) {
      const { data } = await http.post(`/displays/${encodeURIComponent(displayId)}/test`, { kind });
      return data.data;
    },
    async forgetDisplay(displayId) {
      const { data } = await http.delete(`/displays/${encodeURIComponent(displayId)}`);
      return data;
    },
    async getHistory() {
      const { data } = await http.get(p.history);
      return data.data;
    },
    async restore(id) {
      const { data } = await http.post(`${p.restore}/${id}`);
      return data.data;
    },
    // — Gestion des comptes (surface d'@mostajs/app-admin) —
    async listUsers() {
      const { data } = await http.get(p.users);
      return data.data;
    },
    async createUser(user) {
      const { data } = await http.post(p.users, user);
      return data.data;
    },
    async updateUser(id, patch) {
      const { data } = await http.put(`${p.users}/${id}`, patch);
      return data.data;
    },
    async deactivateUser(id) {
      const { data } = await http.patch(`${p.users}/${id}/deactivate`);
      return data.data;
    },
    async deleteUser(id) {
      const { data } = await http.delete(`${p.users}/${id}`);
      return data;
    },
    /**
     * Applique les comptes portés par un article. Crée ceux qui manquent, ne touche à
     * aucun compte existant, et RENVOIE ceux du site absents de l'article — les comptes
     * du client précédent, que l'administrateur seul peut décider de retirer.
     */
    async seedUsers(usersSeed) {
      const { data } = await http.post(`${p.users}/seed`, { users: usersSeed });
      return data.data; // { crees, ignores, refuses, horsArticle }
    },
    // — Connexion au central (plan de contrôle) : réglage local du serveur —
    async getControlPlane() {
      const { data } = await http.get('/control-plane');
      return data.data; // { enabled, url, siteId, hasToken, connected, lastError }
    },
    async saveControlPlane(patch) {
      const { data } = await http.put('/control-plane', patch);
      return data.data;
    },
    async disconnectControlPlane() {
      const { data } = await http.post('/control-plane/disconnect');
      return data.data;
    },

    // — Sauvegardes du site (admin/manager) —
    // Ces appels passent par le MÊME `http` que tout le reste : en LAN (admin.exe) ils vont
    // directement au serveur ; depuis la console centrale, `baseUrl` vaut `/api/sites/<id>/proxy`
    // et le relais WebSocket les exécute sur le serveur du client. Aucune API dédiée au distant :
    // deux API à maintenir, ce sont deux comportements qui divergent en silence.
    async getBackup() {
      const { data } = await http.get('/backup');
      return data.data; // { config, backups[], lastRun }
    },
    async saveBackup(patch) {
      const { data } = await http.put('/backup', patch);
      return data.data;
    },
    async runBackup() {
      const { data } = await http.post('/backup/run');
      return data.data;
    },
    async verifyBackup(path) {
      const { data } = await http.post('/backup/verify', { path });
      return data.data; // { integrity, signature, counts }
    },
    async testRestoreBackup(path) {
      const { data } = await http.post('/backup/test-restore', { path });
      return data.data; // { verifie, attendus, obtenus }
    },
    /**
     * Restauration RÉELLE. `confirm` DOIT valoir le chemin exact — le serveur refuse sinon.
     * Ce n'est pas une formalité côté client : le contrôle est côté SERVEUR, et il tient même
     * face à un `curl`.
     */
    async restoreBackup(path) {
      const { data } = await http.post('/backup/restore', { path, apply: true, confirm: path });
      return data.data;
    },
    // — Licence du serveur (gestion réservée admin) —
    async getLicense() {
      const { data } = await http.get('/license/info');
      return data.data; // { valid, status, project, expiresAt, enforce, offline, hasCode, machineId }
    },
    /** payload : { activationCode?, enforce?, project? } — champs omis = inchangés. */
    async activateLicense(payload) {
      const { data } = await http.post('/license/activate', payload || {});
      return data.data;
    },
    async refreshLicense() {
      const { data } = await http.post('/license/refresh');
      return data.data;
    },
    /** Flux hors-ligne : upload d'une licence signée (téléchargée par l'admin). */
    async importLicense(signed) {
      const { data } = await http.post('/license/import', { license: signed });
      return data.data;
    },
    // NB : la réclamation d'un code (envoi email) n'est PAS relayée au serveur de
    // site — elle part de l'admin (control-plane pour la console, Electron main
    // pour admin.exe). Voir ui/LicenseManager `requestCode`.
    // — Journal d'audit (lecture réservée : permission audit.view) —
    async getAudit(params = {}) {
      const { data } = await http.get(p.audit, { params });
      return data.data; // { entries, total, page, limit }
    },
    /** Télécharge le journal filtré en CSV (Blob) — l'auth passe par l'intercepteur. */
    async downloadAuditCsv(params = {}) {
      const res = await http.get(`${p.audit}/export.csv`, { params, responseType: 'blob' });
      return res.data; // Blob
    },
    /** Upload multipart vers un endpoint déclaré (le champ `mediaUpload` le fournit). */
    async upload(endpoint, file, fields = {}) {
      const form = new FormData();
      form.append('image', file);
      Object.entries(fields).forEach(([k, v]) => form.append(k, v ?? ''));
      const { data } = await http.post(endpoint, form);
      return data.data;
    },
    raw: http,
  };
}
